import * as Sentry from "@sentry/react";

const APP_VERSION = (import.meta.env.VITE_APP_VERSION as string) ?? "unknown";

export type SentryMetadataParams = {
  consultationId: string;
  actor: "user" | "doctor" | "UNKNOWN-USER" | "patient";
  action: string;
  source: string;
  additionalContext?: Record<string, unknown>;
};

export function generateSentryMetadata({
  consultationId,
  actor,
  action,
  source, // = "useSyncConsultationState" "useNotifyConsultation",
  additionalContext = {},
}: SentryMetadataParams) {
  return {
    tags: {
      consultationId,
      actor,
      action,
      appVersion: APP_VERSION,
    },
    contexts: {
      consultation: {
        id: consultationId,
        actor,
        action,
        source,
        appVersion: APP_VERSION,
        ...additionalContext,
      },
    },
  };
}

const extractApolloContext = (error: unknown): Record<string, unknown> => {
  const e = error as Record<string, unknown> | null;
  if (!e) return {};

  const ctx: Record<string, unknown> = {};

  const graphQLErrors = e?.graphQLErrors as
    | Array<{ message: string; extensions?: { code?: string } }>
    | undefined;
  if (graphQLErrors?.length) {
    ctx.graphQLErrors = graphQLErrors.map((err) => ({
      message: err.message,
      code: err.extensions?.code,
    }));
  }

  const networkError = e?.networkError as
    | Record<string, unknown>
    | null
    | undefined;
  if (networkError) {
    const nestedErrors = (networkError?.result as Record<string, unknown>)
      ?.errors as Array<{ message: string }> | undefined;
    ctx.networkError = nestedErrors?.length
      ? nestedErrors.map((err) => err.message).join(", ")
      : networkError?.message;
  }

  return ctx;
};

export const logException = (
  error: string | Error,
  consultationId: string,
  actor: "user" | "doctor" | "UNKNOWN-USER" | "patient",
  action: string,
  source: string,
  additionalContext?: Record<string, unknown>,
) => {
  const exception = error instanceof Error ? error : new Error(error);
  const apolloContext = extractApolloContext(error);

  Sentry.captureException(
    exception,
    generateSentryMetadata({
      consultationId,
      actor,
      action,
      source,
      additionalContext: { ...apolloContext, ...additionalContext },
    }),
  );
};
